/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useMemo } from "react";
import { useNavigate, useParams, Link } from "@tanstack/react-router";
import { useGeofenceQueryV1, useDeleteGeofenceMutationV1 } from "@/api/v1/geofence-api";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  ArrowLeft,
  Edit,
  Trash2,
  AlertTriangle,
  Calendar,
  Users,
  Plane,
  MapPin,
  CheckCircle,
  Activity,
  Info,
} from "lucide-react";
import { Map } from "react-map-gl/maplibre";
import { GeofenceItem } from "@/components/map/geofence-layer";
import { useStore } from "@/store/store";
import { DeleteGeofenceDialog } from "@/components/geofences/delete-dialog";
import { toast } from "sonner";
import { BreadcrumbNav } from "@/components/ui/breadcrumb-nav";
import { useGeofenceViolationsQuery } from "@/api/v0/geofence-violations";
import { addDays, formatDistanceToNow, formatDistance } from "date-fns";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { Badge } from "@/components/ui/badge";
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@/components/ui/empty";

interface FlightSession {
  droneId: string;
  startTime: Date;
  endTime: Date;
  violationCount: number;
}

export function GeofenceDetailRoute() {
  const navigate = useNavigate();
  const params = useParams({ from: "/geofences/$geofenceId" });
  const geofenceId = params.geofenceId;
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [dateRange, setDateRange] = useState<{
    from: Date;
    to: Date;
  }>({
    from: addDays(new Date(), -7),
    to: new Date(),
  });

  const { data: geofence, isLoading, isError } = useGeofenceQueryV1(geofenceId);
  const { data: violations } = useGeofenceViolationsQuery(geofenceId, {
    startTime: dateRange.from?.toISOString(),
    endTime: dateRange.to?.toISOString(),
  });
  const deleteGeofenceMutation = useDeleteGeofenceMutationV1();

  const mapStyleUrl = useStore((state) => state.mapStyleUrl);
  const zoom = useStore((state) => state.mapZoom);

  // Group violations into flight sessions
  const flightSessions = useMemo(() => {
    if (!violations?.drones) return [];

    const sessions: FlightSession[] = [];
    const TIME_GAP_THRESHOLD = 30 * 60 * 1000; // 30 minutes in milliseconds - more generous since we can't check location

    violations.drones.forEach((drone) => {
      const startTime = new Date(drone.firstViolation);
      const endTime = new Date(drone.lastViolation);
      const duration = endTime.getTime() - startTime.getTime();

      // If the duration is longer than our threshold, try to detect actual flight patterns
      if (duration > TIME_GAP_THRESHOLD) {
        // We'll estimate the number of actual flights based on the time range
        // but use a more conservative estimate since we can't check location
        const estimatedFlights = Math.max(
          1,
          Math.ceil(duration / TIME_GAP_THRESHOLD)
        );
        const violationsPerFlight = Math.ceil(
          drone.violationCount / estimatedFlights
        );

        // Create sessions with reasonable time gaps
        let currentTime = startTime.getTime();
        while (currentTime < endTime.getTime()) {
          const sessionEndTime = Math.min(
            currentTime + TIME_GAP_THRESHOLD,
            endTime.getTime()
          );

          sessions.push({
            droneId: drone.droneIdentifier,
            startTime: new Date(currentTime),
            endTime: new Date(sessionEndTime),
            violationCount: Math.min(
              violationsPerFlight,
              drone.violationCount -
                sessions
                  .filter((s) => s.droneId === drone.droneIdentifier)
                  .reduce((acc, s) => acc + s.violationCount, 0)
            ),
          });

          // Add a gap between sessions to represent different flights
          currentTime = sessionEndTime + TIME_GAP_THRESHOLD;
        }
      } else {
        // If duration is less than threshold, treat as single flight
        sessions.push({
          droneId: drone.droneIdentifier,
          startTime,
          endTime,
          violationCount: drone.violationCount,
        });
      }
    });

    return sessions.sort((a, b) => b.endTime.getTime() - a.endTime.getTime());
  }, [violations]);

  // Calculate summary statistics
  const stats = useMemo(() => {
    if (!violations?.drones) return null;

    const uniqueDrones = new Set(flightSessions.map((s) => s.droneId));

    return {
      totalFlights: flightSessions.length,
      uniqueDrones: uniqueDrones.size,
      totalViolations: violations.summary.totalViolations,
    };
  }, [flightSessions, violations]);

  const handleDelete = async () => {
    try {
      await deleteGeofenceMutation.mutateAsync(geofenceId);
      toast.success("Geofence deleted successfully");
      navigate({ to: "/geofences" as any });
    } catch {
      toast.error("Failed to delete geofence");
    }
  };

  if (isLoading) {
    return (
      <main>
        <div className="container mx-auto p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
          <BreadcrumbNav
            items={[
              { label: "Home", href: "/" },
              { label: "Geofences", href: "/geofences" },
              { label: "Loading..." },
            ]}
            className="mb-6"
          />
          <div className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center gap-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="text-muted-foreground">Loading geofence data...</p>
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (isError || !geofence) {
    return (
      <main>
        <div className="container mx-auto p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
          <BreadcrumbNav
            items={[
              { label: "Home", href: "/" },
              { label: "Geofences", href: "/geofences" },
              { label: "Error" },
            ]}
            className="mb-6"
          />
          <Alert className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-2">
                <p className="font-semibold">Failed to load geofence</p>
                <p>
                  The geofence you're looking for could not be found or there
                  was an error loading the data.
                </p>
                <Button
                  onClick={() => navigate({ to: "/geofences" as any })}
                  className="mt-2"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Geofences
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      </main>
    );
  }

  return (
    <main>
      <div className="container mx-auto p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
        <BreadcrumbNav
          items={[
            { label: "Home", href: "/" },
            { label: "Geofences", href: "/geofences" },
            { label: geofence?.name ?? "Geofence" },
          ]}
          className="mb-6"
        />

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="icon"
              onClick={() => navigate({ to: "/geofences" as any })}
              className="h-10 w-10"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-xl sm:text-2xl font-semibold">
                {geofence.name}
              </h1>
              <p className="text-sm text-muted-foreground">
                Created {new Date(geofence.created_at).toLocaleDateString()} •
                Custom airspace boundary
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                navigate({ to: `/geofences/${geofenceId}/violations` as any })
              }
            >
              <AlertTriangle className="h-4 w-4 mr-2" />
              View Violations
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                navigate({ to: `/geofences/${geofenceId}/edit` as any })
              }
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setIsDeleteDialogOpen(true)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid gap-3 sm:gap-4 md:gap-6 grid-cols-2 lg:grid-cols-4">
          <Card className="relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-full -translate-y-16 translate-x-16" />
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 sm:pb-2">
              <CardTitle className="text-xs sm:text-sm font-medium">
                Total Flights
              </CardTitle>
              <Plane className="h-3 w-3 sm:h-4 sm:w-4 text-blue-600" />
            </CardHeader>
            <CardContent className="pt-2 sm:pt-0">
              <div className="text-lg sm:text-2xl font-bold">
                {stats?.totalFlights ?? 0}
              </div>
              <p className="text-xs text-muted-foreground">
                Flight sessions detected
              </p>
            </CardContent>
          </Card>

          <Card className="relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-full -translate-y-16 translate-x-16" />
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 sm:pb-2">
              <CardTitle className="text-xs sm:text-sm font-medium">
                Violations
              </CardTitle>
              <AlertTriangle className="h-3 w-3 sm:h-4 sm:w-4 text-red-600" />
            </CardHeader>
            <CardContent className="pt-2 sm:pt-0">
              <div className="text-lg sm:text-2xl font-bold text-red-600">
                {stats?.totalViolations ?? 0}
              </div>
              <p className="text-xs text-muted-foreground">Total violations</p>
            </CardContent>
          </Card>

          <Card className="relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-orange-500/10 to-red-500/10 rounded-full -translate-y-16 translate-x-16" />
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 sm:pb-2">
              <CardTitle className="text-xs sm:text-sm font-medium">
                Unique Drones
              </CardTitle>
              <Users className="h-3 w-3 sm:h-4 sm:w-4 text-orange-600" />
            </CardHeader>
            <CardContent className="pt-2 sm:pt-0">
              <div className="text-lg sm:text-2xl font-bold">
                {stats?.uniqueDrones ?? 0}
              </div>
              <p className="text-xs text-muted-foreground">
                Different aircraft
              </p>
            </CardContent>
          </Card>

          <Card className="relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-full -translate-y-16 translate-x-16" />
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 sm:pb-2">
              <CardTitle className="text-xs sm:text-sm font-medium">
                Status
              </CardTitle>
              <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 text-green-600" />
            </CardHeader>
            <CardContent className="pt-2 sm:pt-0">
              <div className="text-lg sm:text-2xl font-bold text-green-600">
                Active
              </div>
              <p className="text-xs text-muted-foreground">
                Monitoring enabled
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="grid gap-4 sm:gap-6 lg:grid-cols-2">
          {/* Left Column - Details and Settings */}
          <div className="space-y-4 sm:space-y-6">
            {/* Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Info className="h-5 w-5" />
                  Geofence Details
                </CardTitle>
                <CardDescription>
                  Basic information about this airspace boundary
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm font-medium">Type</p>
                  <Badge variant="secondary" className="mt-1">
                    {geofence.type}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm font-medium">Created</p>
                  <p className="text-base font-normal">
                    {new Date(geofence.created_at).toLocaleString()}
                  </p>
                </div>
                {geofence.description && (
                  <div>
                    <p className="text-sm font-medium">Description</p>
                    <p className="text-base font-normal">
                      {geofence.description}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Map and Activity */}
          <div className="space-y-4 sm:space-y-6">
            {/* Map Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  Location
                </CardTitle>
                <CardDescription>
                  Visual representation of the geofence boundary
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <div className="h-[400px] relative">
                  <Map
                    id="geofence-map"
                    mapStyle={mapStyleUrl}
                    initialViewState={{
                      longitude: geofence.geometry[0][0][0],
                      latitude: geofence.geometry[0][0][1],
                      zoom: zoom as number,
                    }}
                    style={{ width: "100%", height: "100%" }}
                    mapLib={import("maplibre-gl")}
                    attributionControl={false}
                    reuseMaps
                  >
                    <GeofenceItem geofence={geofence} />
                  </Map>
                </div>
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  Flight Sessions
                </CardTitle>
                <CardDescription>
                  Recent drone activity in this geofence area
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
                  <div className="flex-1">
                    <DateRangePicker
                      value={dateRange}
                      onChange={(date) => {
                        if (date?.from && date?.to) {
                          setDateRange({
                            from: date.from,
                            to: date.to,
                          });
                        }
                      }}
                    />
                  </div>
                </div>

                {flightSessions.length > 0 ? (
                  <div className="space-y-4">
                    {flightSessions.map((session, index) => (
                      <div
                        key={`${session.droneId}-${index}`}
                        className="flex items-center justify-between py-3 px-4 rounded-lg bg-muted/50"
                      >
                        <div className="flex items-center">
                          <Plane className="h-4 w-4 mr-3 text-muted-foreground" />
                          <div>
                            <p className="font-medium">{session.droneId}</p>
                            <p className="text-sm text-muted-foreground">
                              {formatDistanceToNow(session.endTime, {
                                addSuffix: true,
                              })}{" "}
                              • Duration:{" "}
                              {formatDistance(
                                session.startTime,
                                session.endTime
                              )}{" "}
                              •{session.violationCount} violations
                            </p>
                          </div>
                        </div>
                        <Badge variant="destructive">
                          {session.violationCount}
                        </Badge>
                      </div>
                    ))}
                    <div className="text-center pt-2">
                      <Button variant="ghost" size="sm" asChild>
                        <Link
                          to={`/geofences/${geofenceId}/violations` as any}
                          className="text-primary hover:underline"
                        >
                          View full violation history
                        </Link>
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Empty>
                    <EmptyMedia variant="icon">
                      <Calendar className="h-6 w-6" />
                    </EmptyMedia>
                    <EmptyHeader>
                      <EmptyTitle>No violations found</EmptyTitle>
                      <EmptyDescription>
                        No drone activity detected in this geofence for the
                        selected date range.
                      </EmptyDescription>
                    </EmptyHeader>
                  </Empty>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Help Information */}
        <Alert className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950">
          <Info className="h-4 w-4" />
          <AlertDescription>
            <div className="space-y-2">
              <p className="font-semibold">Geofence Management</p>
              <p>
                This geofence is actively monitoring drone activity in the
                defined area. View violations to see detailed activity reports,
                or edit the geofence to modify its boundaries and settings.
              </p>
            </div>
          </AlertDescription>
        </Alert>

        <DeleteGeofenceDialog
          isOpen={isDeleteDialogOpen}
          isDeleting={deleteGeofenceMutation.isPending}
          onOpenChange={setIsDeleteDialogOpen}
          onConfirm={handleDelete}
          geofenceName={geofence.name}
        />
      </div>
    </main>
  );
}
